import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TlkModalWindowRef, TLK_MODAL_WINDOW_DATA, TlkModalWindowModule } from '@itero/ui-toolkit-angular/modal-window';
import { TlkTextInputComponent } from '@itero/ui-toolkit-angular/text-input';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { TlkBadgeComponent } from '@itero/ui-toolkit-angular/badge';
import { TlkSelectComponent } from '@itero/ui-toolkit-angular/select';
import { SearchIconComponent } from '@itero/ui-toolkit-angular/icons';
import { DelayedSpinnerComponent } from '../delayed-spinner/delayed-spinner.component';
import { PatientAvatarComponent } from '../patient-avatar/patient-avatar.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Patient } from '../../state-machine/patient-forms.types';
import translations from '../../assets/i18n/en.json';
import { PatientFormsMachineService, PatientFormsMachineState } from '../../services/patient-forms-machine.service';
import { UNIFIED_PATIENTS_CONFIG, type UnifiedPatientsConfig } from '../../services/unified-patients-config.token';
import { DobFormatPipe } from '../../services/dob-format.service';
import { PatientThumbnailService } from '../../services/patient-thumbnail.service';

export interface PatientSearchData {
	recentPatients$: Promise<Patient[]>;
	matchingPatients?: Patient[];
	mode: 'recent' | 'matches';
}

type SystemFilter = 'all' | 'iTero' | 'Invisalign';

@Component({
	selector: 'rx-patient-search-content',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		TlkModalWindowModule,
		TlkTextInputComponent,
		TlkButtonComponent,
		TlkBadgeComponent,
		TlkSelectComponent,
		SearchIconComponent,
		DelayedSpinnerComponent,
		PatientAvatarComponent,
		TranslateModule,
		DobFormatPipe
	],
	templateUrl: './patient-search.component.html',
	styleUrls: ['./patient-search.component.scss']
})
export class PatientSearchContentComponent implements OnInit, OnDestroy {
	query = '';
	systemFilter: { id: SystemFilter; name: string };
	selectedPatient: Patient | null = null;
	displayedPatients: Patient[] = [];
	loading = false;
	thumbnailUrls = new Map<string, string>();

	private recentPatients: Patient[] = [];
	private query$ = new Subject<string>();
	private stateSubscription!: Subscription;

	filterOptions: { id: SystemFilter; name: string }[];
	getFilterId = (_index: number, opt: { id: string }) => opt.id;
	getFilterName = (opt: { name: string }) => opt.name;

	constructor(
		public modalWindowRef: TlkModalWindowRef<Patient | undefined>,
		@Inject(TLK_MODAL_WINDOW_DATA) public data: PatientSearchData,
		private readonly machine: PatientFormsMachineService,
		@Inject(UNIFIED_PATIENTS_CONFIG) public config: UnifiedPatientsConfig,
		private readonly translate: TranslateService,
		private readonly thumbnailService: PatientThumbnailService
	) {
		this.filterOptions = [
			{ id: 'all', name: translations.PatientSearch.AllPatients },
			{ id: 'iTero', name: 'iTero' },
			{ id: 'Invisalign', name: 'Invisalign' }
		];
		this.systemFilter = this.filterOptions[0];
	}

	tr(key: string): string {
		const [section, prop] = key.split('.');
		return (translations as Record<string, Record<string, string>>)[section]?.[prop] ?? key;
	}

	get isBrowsingRecent(): boolean {
		return this.machine.state.matches('patientSearch.browsingRecent');
	}

	get isSearchingMatches(): boolean {
		return this.data.mode === 'matches';
	}

	ngOnInit(): void {
		if (this.data.mode === 'matches') {
			this.displayedPatients = this.data.matchingPatients || [];
			this.loadThumbnails();
		} else {
			this.loading = true;
			void this.data.recentPatients$.then(patients => {
				this.recentPatients = patients;
				this.displayedPatients = patients;
				this.loading = false;
				this.loadThumbnails();
			});
		}

		this.query$.pipe(debounceTime(400)).subscribe(() => {
			this.triggerSearch();
		});

		this.stateSubscription = this.machine.state$.subscribe((state: PatientFormsMachineState) => {
			if (state.matches('patientSearch.searchingDB') && state.event?.type === 'SEARCH.RESULTS') {
				this.loading = false;
				this.displayedPatients = state.context.searchResults;
				this.loadThumbnails();
			}
		});
	}

	ngOnDestroy(): void {
		this.query$.complete();
		this.stateSubscription.unsubscribe();
	}

	onQueryChange(): void {
		this.query$.next(this.query);
	}

	onFilterChange(): void {
		this.triggerSearch();
	}

	selectRow(patient: Patient): void {
		this.selectedPatient = patient;
	}

	confirm(): void {
		if (this.selectedPatient) {
			this.modalWindowRef.close(this.selectedPatient);
		}
	}

	onThumbnailError(patientId: string | null): void {
		if (patientId) {
			this.thumbnailUrls.delete(patientId);
		}
	}

	private triggerSearch(): void {
		if (this.query.length >= 3) {
			this.loading = true;
			this.machine.send({ type: 'SEARCH.QUERY_ENTERED', query: this.query, filter: this.systemFilter.id });
		} else {
			this.loading = false;
			this.machine.send({ type: 'SEARCH.QUERY_CLEARED' });
			this.displayedPatients = this.recentPatients;
			this.loadThumbnails();
		}
	}

	private loadThumbnails(): void {
		if (this.displayedPatients.length === 0) {
			return;
		}

		const eligibleIds = this.displayedPatients.filter(p => p.invisalignId).map(p => p.invisalignId as string);

		if (eligibleIds.length === 0) {
			return;
		}

		this.thumbnailService.getThumbnails(eligibleIds).subscribe(urlMap => {
			urlMap.forEach((url, id) => this.thumbnailUrls.set(id, url));
		});
	}
}
